import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Popover from '@mui/material/Popover';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Dialog from '/@/components/dialog';
import Drawing from '/@/components/drawing';
import Popup from '/@/components/popup';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { setSelectedFeature as setSelectedFeatureAction } from '/@/store/nav/actions';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

const MyRecommendationsMobileCard = ({
    headers,
    data,
    cols = 3,
    className,
    setOpenSnack,
    setStep,
    setFilterRecos,
    setSearchInput,
    isCropMonitoring = false,
    setSelected,
    deleteRecommendations,
}) => {
    const [headerRows, setHeaderRows] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [showPopupDelete, setShowPopupDelete] = useState(false);
    const history = useNavigate();
    const dispatch = useDispatch();
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const [openModal, setOpenModal] = useState(false);
    const setSelectedFeature = bindActionCreators(setSelectedFeatureAction, dispatch);

    useEffect(() => {
        let i = 0;
        const newHeaderRows = [];

        while (i < headers.length) {
            newHeaderRows.push(headers.slice(i, i + cols));
            i += cols;
        }

        setHeaderRows(newHeaderRows);
    }, [headers, cols]);

    const handleClickOptions = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseOptions = () => {
        setAnchorEl(null);
    };

    const goToReco = () => {
        // The row carries the handler that knows where each recommendation type opens; building
        // a URL here used `data.type` ('hybrid' | 'weed' | ...) where the route expects a crop.
        data.onClick?.(data);
    };

    const handleDeleteRecommendation = (recoId) => {
        (async () => {
            await deleteRecommendations({ ids: [recoId] });
        })();
        setShowPopupDelete(false);
        if (setOpenSnack) setOpenSnack(true);
        setFilterRecos({});
        if (setStep) {
            setStep('step1');
        }
        setSearchInput('');
    };

    const handleClickDelete = () => {
        setShowPopupDelete(true);
        setAnchorEl(null);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        goToReco();
    };

    const handleCreateNewReco = () => {
        setOpenModal(false);
        setSelectedFeature({ view: 'Home', selectedFeature: 'recommendation', crop: 'corn' });
        history(reverse('Home'));
    };

    return (
        <div className={classNames(styles.root, className)}>
            <table className={styles.table}>
                <tbody>
                    {headerRows.map((headerRow, i) => (
                        <Fragment key={i}>
                            <tr
                                className={classNames(styles.row, styles.header, styles[`color-${settings.app.theme}`])}
                            >
                                {headerRow.map((header) => (
                                    <th className={styles.cell} key={header.id}>
                                        {header.name}
                                    </th>
                                ))}
                            </tr>
                            <tr className={styles.row}>
                                {headerRow.map((header) => (
                                    <th className={styles.cell} key={header.id}>
                                        {header.id != 'product'
                                            ? data[header.id]
                                            : data.product.map((product) => <div key={product}>{product}</div>)}
                                    </th>
                                ))}
                            </tr>
                        </Fragment>
                    ))}
                </tbody>
            </table>
            <div className={styles.containerButtonIcon}>
                {isCropMonitoring ? (
                    <Button
                        variant='contained'
                        onClick={() => {
                            setSelected(data.id);
                        }}
                        sx={{ mt: 1 }}
                    >
                        {_('Seleccionar')}
                    </Button>
                ) : (
                    <Fragment>
                        <div className={styles.containerButton}>
                            <Button
                                variant='outlined'
                                onClick={() => {
                                    data.isOldRecommendation && !isCropMonitoring ? setOpenModal(true) : goToReco();
                                }}
                                fullWidth
                                sx={{ mt: 1 }}
                                endIcon={
                                    data.isOldRecommendation && !isCropMonitoring && <ErrorOutlineIcon color='error' />
                                }
                            >
                                {_('Ver')}
                            </Button>
                        </div>
                        <div className={styles.containerIcon}>
                            <span className={classNames('material-icons', styles.icon)} onClick={handleClickOptions}>
                                more_vert
                            </span>
                            <Popover
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleCloseOptions}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                            >
                                <div className={styles.containerPopover}>
                                    <div className={styles.itemsPopover}>
                                        <Title className={styles.options} onClick={handleClickDelete}>
                                            {_('Eliminar')}
                                        </Title>
                                    </div>
                                </div>
                            </Popover>
                        </div>
                    </Fragment>
                )}
            </div>
            <Dialog
                title={_('Eliminar')}
                message={_('¿Estás seguro que querés eliminar la recomendación?')}
                visible={showPopupDelete}
                onClose={() => setShowPopupDelete(false)}
                onCancel={() => setShowPopupDelete(false)}
                onAccept={() => handleDeleteRecommendation(data.id)}
                alignCenter
            />
            <Popup visible={openModal} borderRadius showCloseIcon onClose={handleCloseModal}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
                    <Drawing type='library-not-found' coloredTitle size='sm' title={_('Atención')} />

                    <Title align='center' size='md'>
                        {_(
                            'Esta recomendación fue creada en una campaña anterior, y los datos podrían estar desactualizados. En el caso de querer información más reciente, te aconsejamos crear una nueva.'
                        )}
                    </Title>

                    <Grid container sx={{ gap: 2 }} direction='column'>
                        <Grid item xs={12}>
                            <Button variant='outlined' onClick={handleCloseModal} fullWidth>
                                {_('Ver recomendación')}
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant='contained' onClick={handleCreateNewReco} fullWidth>
                                {_('Crear Nueva Recomendación')}
                            </Button>
                        </Grid>
                    </Grid>
                </div>
            </Popup>
        </div>
    );
};

export default MyRecommendationsMobileCard;
